import React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import EditablePill from './EditablePill';

/**
 * Combined row: label + inline editable pill (right-aligned) + slider below.
 * Optional segmented suffix (e.g. Yr/Mo) sits next to the pill.
 */
export default function SliderRow({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  pillProps = {},
  suffixOptions,
  suffixLabels,
  activeSuffix,
  onSuffixChange,
  trackTone = 'blue',
  helperLeft,
  helperRight,
}) {
  const trackClasses = {
    blue: 'bg-[#EAF5FE] dark:bg-[#1E3A5F]',
  }[trackTone];
  const rangeClasses = {
    blue: 'bg-gradient-to-r from-[#0EA5FF] to-[#0176D3]',
  }[trackTone];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm sm:text-base text-slate-700 dark:text-slate-200 font-semibold">{label}</label>
        <div className="flex items-center gap-2">
          <EditablePill
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            {...pillProps}
          />
          {suffixOptions && (
            <div className="flex rounded-lg overflow-hidden border-2 border-slate-200 dark:border-[#1E2A44] shadow-sm">
              {suffixOptions.map((opt, idx) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onSuffixChange(opt)}
                  className={`px-3 py-2 text-sm font-semibold transition-colors ${
                    activeSuffix === opt
                      ? 'bg-[#0176D3] text-white'
                      : 'bg-white dark:bg-[#0F1B36] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#152343]'
                  }`}
                  data-testid={`slider-suffix-${opt}`}
                >
                  {suffixLabels ? suffixLabels[idx] : opt}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <SliderPrimitive.Root
        min={min}
        max={max}
        step={step}
        value={[Math.min(max, Math.max(min, Number(value) || min))]}
        onValueChange={(v) => onChange(v[0])}
        className="relative flex w-full touch-none select-none items-center h-6"
        data-testid={`slider-${String(label || '').toLowerCase().replace(/\s+/g, '-')}`}
      >
        <SliderPrimitive.Track className={`relative h-2 w-full grow overflow-hidden rounded-full ${trackClasses}`}>
          <SliderPrimitive.Range className={`absolute h-full ${rangeClasses}`} />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          className="block h-5 w-5 rounded-full border-2 border-[#0176D3] bg-white shadow-md hover:shadow-lg transition-shadow focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#B7E1FA]"
          aria-label={label}
        />
      </SliderPrimitive.Root>

      {(helperLeft || helperRight) && (
        <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 -mt-1">
          <span>{helperLeft}</span>
          <span>{helperRight}</span>
        </div>
      )}
    </div>
  );
}
